import React, { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Building2,
  ExternalLink,
  Globe2,
  Search,
} from "lucide-react";

import api from "@/lib/api";

const titleCase = (value = "") =>
  String(value)
    .replaceAll("_", " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

export default function AgencyPartnerDiscovery() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState("real estate agencies");
  const [country, setCountry] = useState("Portugal");
  const [region, setRegion] = useState("");
  const [city, setCity] = useState("");
  const [specialisation, setSpecialisation] = useState("");
  const [officialUrl, setOfficialUrl] = useState("");
  const [discovery, setDiscovery] = useState(null);
  const [imported, setImported] = useState({});

  const metaQuery = useQuery({
    queryKey: ["agency-partners-meta"],
    queryFn: async () =>
      (await api.get("/agency-partners/meta")).data,
  });

  const discoverMutation = useMutation({
    mutationFn: async () =>
      (
        await api.post("/agency-partners/web/discover", {
          search_term: searchTerm,
          country,
          region,
          city,
          specialisation,
          official_url: officialUrl,
          language: i18n.language || "en",
        })
      ).data,
    onSuccess: (data) => setDiscovery(data),
  });

  const importMutation = useMutation({
    mutationFn: async (candidate) =>
      (
        await api.post("/agency-partners/web/import", {
          candidate,
        })
      ).data,

    onSuccess: (_, candidate) => {
      setImported((current) => ({
        ...current,
        [candidate.candidate_id]: true,
      }));
    },
  });

  const results = discovery?.results || [];

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <button
            onClick={() => navigate("/agency-partners")}
            className="mb-3 inline-flex items-center gap-2 text-sm text-slate-600"
          >
            <ArrowLeft size={16} />
            {t(
              "agencyPartners.discovery.back",
              "Agency Partner Network"
            )}
          </button>

          <h1 className="text-3xl font-semibold text-slate-900">
            {t(
              "agencyPartners.discovery.title",
              "Find Real Estate Agencies"
            )}
          </h1>

          <p className="mt-2 max-w-3xl text-sm text-slate-600">
            {t(
              "agencyPartners.discovery.subtitle",
              "Discover professional real-estate agencies by market, location and property specialisation. Review each candidate before importing it into the Panatlantik network."
            )}
          </p>
        </div>

        <Globe2 size={38} className="text-slate-400" />
      </div>

      <div className="rounded-2xl border bg-white p-5">
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          <Field
            label={t(
              "agencyPartners.discovery.searchTerm",
              "Agency / search term"
            )}
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Luxury real estate agencies"
          />

          <Field
            label={t("agencyPartners.discovery.country", "Country")}
            value={country}
            onChange={setCountry}
            placeholder="Portugal"
          />

          <Field
            label={t("agencyPartners.discovery.region", "Region")}
            value={region}
            onChange={setRegion}
            placeholder="Algarve"
          />

          <Field
            label={t("agencyPartners.discovery.city", "City")}
            value={city}
            onChange={setCity}
            placeholder="Faro"
          />

          <label className="block">
            <span className="mb-1 block text-sm font-medium">
              {t(
                "agencyPartners.discovery.specialisation",
                "Property specialisation"
              )}
            </span>

            <select
              value={specialisation}
              onChange={(e) => setSpecialisation(e.target.value)}
              className="w-full rounded-lg border px-3 py-2"
            >
              <option value="">
                {t(
                  "agencyPartners.discovery.allSpecialisations",
                  "All specialisations"
                )}
              </option>

              {(metaQuery.data?.property_specialisations || []).map(
                (item) => (
                  <option key={item} value={item}>
                    {t(
                      `agencyPartners.specialisations.${item}`,
                      titleCase(item)
                    )}
                  </option>
                )
              )}
            </select>
          </label>

          <Field
            label={t(
              "agencyPartners.discovery.officialUrl",
              "Known official website (optional)"
            )}
            value={officialUrl}
            onChange={setOfficialUrl}
            placeholder="https://..."
          />
        </div>

        <div className="mt-5">
          <button
            onClick={() => discoverMutation.mutate()}
            disabled={discoverMutation.isPending}
            className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-5 py-2.5 text-sm font-medium text-white disabled:opacity-50"
          >
            <Search size={17} />

            {discoverMutation.isPending
              ? t(
                  "agencyPartners.discovery.searching",
                  "Searching…"
                )
              : t(
                  "agencyPartners.discovery.search",
                  "Find agencies"
                )}
          </button>
        </div>
      </div>

      {discoverMutation.isError && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {discoverMutation.error?.response?.data?.detail ||
            discoverMutation.error?.message ||
            t(
              "agencyPartners.discovery.searchError",
              "Agency discovery failed."
            )}
        </div>
      )}

      {discovery && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <span className="font-semibold">{discovery.count || 0}</span>{" "}
            {t(
              "agencyPartners.discovery.results",
              "agency candidates found"
            )}
          </div>

          <div className="text-sm text-slate-500">
            {t("agencyPartners.discovery.provider", "Provider")}:{" "}
            <strong>{discovery.provider || "—"}</strong>
          </div>
        </div>
      )}

      {discovery && results.length === 0 && (
        <div className="rounded-2xl border bg-white p-10 text-center">
          <Building2
            size={38}
            className="mx-auto mb-3 text-slate-400"
          />

          <p className="text-slate-600">
            {t(
              "agencyPartners.discovery.noResults",
              "No agency candidates were found. Try another city, region or search term."
            )}
          </p>
        </div>
      )}

      <div className="grid gap-4 xl:grid-cols-2">
        {results.map((candidate) => {
          const isImported =
            candidate.already_imported ||
            imported[candidate.candidate_id];

          return (
            <article
              key={candidate.candidate_id}
              className="rounded-2xl border bg-white p-5 shadow-sm"
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">
                    {candidate.name || candidate.domain}
                  </h2>

                  <div className="mt-1 text-sm text-slate-500">
                    {[candidate.city, candidate.region, candidate.country]
                      .filter(Boolean)
                      .join(", ")}
                  </div>
                </div>

                {candidate.official_url && (
                  <a
                    href={candidate.official_url}
                    target="_blank"
                    rel="noreferrer"
                    className="rounded-lg border p-2"
                    title={t(
                      "agencyPartners.discovery.openWebsite",
                      "Open website"
                    )}
                  >
                    <ExternalLink size={16} />
                  </a>
                )}
              </div>

              {candidate.description && (
                <p className="mt-4 line-clamp-4 text-sm leading-6 text-slate-600">
                  {candidate.description}
                </p>
              )}

              <div className="mt-4 flex flex-wrap gap-2">
                {(candidate.property_specialisations || []).map(
                  (item) => (
                    <span
                      key={item}
                      className="rounded-full bg-slate-100 px-3 py-1 text-xs"
                    >
                      {t(
                        `agencyPartners.specialisations.${item}`,
                        titleCase(item)
                      )}
                    </span>
                  )
                )}
              </div>

              <div className="mt-5 grid gap-3 border-t pt-4 text-sm sm:grid-cols-2">
                <Info
                  label={t(
                    "agencyPartners.discovery.domain",
                    "Domain"
                  )}
                  value={candidate.domain}
                />

                <Info
                  label={t(
                    "agencyPartners.discovery.email",
                    "Professional email"
                  )}
                  value={candidate.contact_email}
                />

                <Info
                  label={t(
                    "agencyPartners.discovery.phone",
                    "Telephone"
                  )}
                  value={candidate.contact_phone}
                />

                <Info
                  label={t(
                    "agencyPartners.discovery.verification",
                    "Verification"
                  )}
                  value={titleCase(
                    candidate.verification_status
                  )}
                />
              </div>

              <div className="mt-5 flex items-center justify-between gap-3">
                <div className="text-xs text-slate-500">
                  {t(
                    "agencyPartners.discovery.reviewNotice",
                    "Public web candidate — review before cooperation."
                  )}
                </div>

                <button
                  disabled={
                    isImported ||
                    importMutation.isPending
                  }
                  onClick={() =>
                    importMutation.mutate(candidate)
                  }
                  className="rounded-lg border px-4 py-2 text-sm font-medium disabled:bg-slate-100 disabled:text-slate-400"
                >
                  {isImported
                    ? t(
                        "agencyPartners.discovery.imported",
                        "Imported"
                      )
                    : t(
                        "agencyPartners.discovery.import",
                        "Import agency"
                      )}
                </button>
              </div>
            </article>
          );
        })}
      </div>

      {importMutation.isError && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {importMutation.error?.response?.data?.detail ||
            importMutation.error?.message}
        </div>
      )}
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder = "",
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium">
        {label}
      </span>

      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-lg border px-3 py-2"
      />
    </label>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        {label}
      </div>
      <div className="mt-1 break-words text-slate-800">
        {value || "—"}
      </div>
    </div>
  );
}
